import React from 'react';
import { useNavigate, useLocation, Routes, Route, Navigate, Link as RouterLink } from 'react-router-dom';
import Layout from '../../components/Layout';
import PermissionPage from './usernavTabPages/PermissionPage';
import Users from './usernavTabPages/Users';
import Role from './usernavTabPages/role';
import LoginLogNavTabPage from './usernavTabPages/LoginLogPage';
import { Tabs, Tab, Box, Breadcrumbs, Typography, Link } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const tabRoutes = [
  { label: 'Users', value: '/masters/user/users', icon: <PersonIcon /> },
  { label: 'Role', value: '/masters/user/role', icon: <SecurityIcon /> },
  { label: 'Permission', value: '/masters/user/permission', icon: <VerifiedUserIcon /> },
  { label: 'Login Logs', value: '/masters/user/login-logs', icon: <AccessTimeIcon sx={{ color: '#757575' }} /> },
];

const segmentLabels = {
  masters: 'Masters',
  user: 'User',
  users: 'Users',
  role: 'Role',
  permission: 'Permission',
};

const buildPath = (segments, idx) => '/' + segments.slice(0, idx + 1).join('/');

const UserMasterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentTab = tabRoutes.find(tab => location.pathname.startsWith(tab.value))?.value || '/masters/user/users';
  const handleChange = (event, newValue) => {
    if (newValue !== currentTab) {
      navigate(newValue);
    }
  };

  const pathSegments = location.pathname.split('/').filter(Boolean).slice(0, 4);

  return (
    <Layout>
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" sx={{ color: '#00C853' }} />} // Parrot green separator
        aria-label="breadcrumb"
        sx={{ mb: 2, mt: 1 }}
      >
        {pathSegments.map((seg, idx) => {
          const label = segmentLabels[seg] || (seg.charAt(0).toUpperCase() + seg.slice(1));
          const isLast = idx === pathSegments.length - 1;
          const to = buildPath(pathSegments, idx);
          return isLast ? (
            <Typography
              key={to}
              sx={{
                color: '#111', // Black for active
                fontWeight: 700,
                fontSize: 18,
                letterSpacing: 1,
                fontFamily: 'inherit',
              }}
            >
              {label}
            </Typography>
          ) : (
            <Link
              key={to}
              component={RouterLink}
              to={to}
              underline="hover"
              sx={{
                color: '#111', // Black for inactive
                fontWeight: 500,
                fontSize: 16,
                letterSpacing: 1,
                fontFamily: 'inherit',
                transition: 'color 0.3s ease',
                '&:hover': { 
                  color: '#111', // Black on hover
                  fontWeight: 600,
                },
              }}
            >
              {label}
            </Link>
          );
        })}
      </Breadcrumbs>

      {/* Tabs */}
      <Tabs
        value={currentTab}
        onChange={handleChange}
        textColor="primary"
        indicatorColor="primary"
        sx={{
          minHeight: 56,
          borderBottom: '1.5px solid #e5e7eb',
          px: 0,
          '& .MuiTabs-indicator': {
            height: 4,
            borderRadius: 2,
            background: '#00C853', // Parrot green indicator
            left: 0,
            right: 0,
          },
        }}
      >
        {tabRoutes.map(tab => (
          <Tab
            key={tab.value}
            label={
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  fontSize: 24, 
                  color: '#757575', // grey icon
                  transition: 'color 0.3s ease',
                }}>
                  {tab.icon}
                </Box>
                <Box sx={{ 
                  fontWeight: currentTab === tab.value ? 700 : 500, 
                  fontSize: 16, 
                  color: '#111', // black for all
                  letterSpacing: 0.5,
                  transition: 'all 0.3s ease',
                }}>
                  {tab.label}
                </Box>
              </Box>
            }
            value={tab.value}
            sx={{
              minWidth: 120,
              minHeight: 56,
              py: 2,
              px: 3,
              textTransform: 'none',
              borderRadius: '8px 8px 0 0',
              background: 'transparent',
              boxShadow: 'none',
              mr: 2,
              transition: 'all 0.3s ease',
              '&.Mui-selected': {
                background: 'transparent',
                boxShadow: 'none',
                color: '#111',
                fontWeight: 700,
              },
              '&:hover': {
                background: '#E8F5E9', // Subtle green hover
                borderRadius: '8px 8px 0 0',
              },
            }}
          />
        ))}
      </Tabs>

      {/* Tab Content */}
      <Routes>
        <Route path="users" element={<Users />} />
        <Route path="role" element={<Role />} />
        <Route path="permission" element={<PermissionPage />} />
        <Route path="login-logs" element={<LoginLogNavTabPage />} />
        <Route path="*" element={<Navigate to="users" />} />
      </Routes>
    </Layout>
  );
};

export default UserMasterPage;
